import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import notifiers from "global/constants/NotificationConstants";
import viewpaths from "global/constants/ViewPathConstants";
import title from "global/constants/TitleConstants";
import {
  formatDateInLongDateTimeFormat,
  formateRelativeTimeFormat,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import history from "utils/history";
import { useTitle } from "utils/UseTitle";
import SettingsLayout from "screens/Settings/components/SettingsLayout";
import { doFetchTicketById } from "screens/Tickets/ViewTicket/ViewTicketService";
import Comments from "./Components/Comments";
import TicketDetails from "./Components/TicketDetails";
import TicketReporterDetails from "./Components/TicketReporterDetails";
import { BackArrowIcon } from "./Components/ViewTicketIcons";
import viewTicketTokens from "./ViewTicket.tokens";
import {
  backLinkSx,
  bodySx,
  headerSx,
  mainColumnSx,
  metaSx,
  railSx,
  statusPillSx,
  titleRowSx,
  titleSx,
} from "./ViewTicket.styles";
import { LiveRegion } from "components/live-region";

const { header } = viewTicketTokens;

/**
 * A single support ticket. It renders inside the Settings shell rather than the
 * centred app layout, so opening a ticket swaps only the content column and
 * leaves the settings navigation — and the reader's sense of place — intact.
 */
const ViewTicket = () => {
  useTitle(title.TICKETS);
  const { id } = useParams<{ id: string }>();
  const tabKey = new URLSearchParams(useLocation().search).get("tab");
  const [isLoading, setIsLoading] = useState(false);
  const [ticket, setTicket] = useState<any>(null);

  // The route carried no title of its own, so the tab kept whichever title the
  // previous screen had set (WCAG 2.4.2).
  useTitle(
    ticket?.name
      ? `[#${ticket.id}] ${ticket.name}${title.FormattedTitleEnd}`
      : title.TICKETS,
  );

  useEffect(() => {
    fetchTicketById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTicketById = async () => {
    try {
      setIsLoading(true);
      const response = await doFetchTicketById(id);
      setTicket(response);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    } finally {
      setIsLoading(false);
    }
  };

  /** Returns to the list on the tab the ticket was opened from. */
  const handleBack = () => {
    history.push(
      tabKey
        ? `${viewpaths.ticketsViewPath}?tab=${tabKey}`
        : viewpaths.ticketsViewPath,
    );
  };

  const getMeta = () => {
    const created = ticket?.activationDate
      ? `Created ${formatDateInLongDateTimeFormat(ticket.activationDate)}`
      : "";
    const updated = ticket?.updatedOn
      ? `Updated ${formateRelativeTimeFormat(ticket.updatedOn)}`
      : "";
    return [created, updated].filter(Boolean).join(" · ");
  };

  const getHeader = () => (
    <Box sx={headerSx}>
      <Button disableRipple onClick={handleBack} sx={backLinkSx}>
        <BackArrowIcon
          sx={{ width: header.backIconSize, height: header.backIconSize }}
        />
        Back to tickets
      </Button>

      {isLoading && !ticket ? (
        <>
          <Skeleton animation="wave" variant="text" height={28} width={420} />
          <Skeleton animation="wave" variant="text" height={16} width={260} />
        </>
      ) : (
        <>
          <Box sx={titleRowSx}>
            <Typography component="h1" sx={titleSx}>
              [#{ticket?.id}] {ticket?.name}
            </Typography>
            {ticket?.status && (
              <Box component="span" sx={statusPillSx(ticket.status)}>
                {ticket.status}
              </Box>
            )}
          </Box>
          <Typography component="p" sx={metaSx}>
            {getMeta()}
          </Typography>
        </>
      )}
    </Box>
  );

  return (
    <SettingsLayout activeTab="Support Tickets">
      <Box>
        {getHeader()}

        <Box sx={bodySx}>
          <Box sx={mainColumnSx}>
            <TicketReporterDetails
              ticketDetails={ticket}
              isLoading={isLoading}
            />
            <Comments
              ticketDetails={ticket}
              isLoading={isLoading}
              ticketId={id}
              fetchTicketById={fetchTicketById}
            />
          </Box>

          <Box sx={railSx}>
            <TicketDetails ticketDetails={ticket} isLoading={isLoading} />
          </Box>
        </Box>
      </Box>

      {/*
       * The whole screen swaps from skeletons to content with nothing to
       * announce it (WCAG 4.1.3).
       */}
      <LiveRegion
        id="view-ticket-status"
        message={
          isLoading
            ? "Loading ticket"
            : ticket?.id
              ? `Ticket ${ticket.id} loaded`
              : ""
        }
      />
    </SettingsLayout>
  );
};

export default ViewTicket;
